/**
 * PreloadScene - Load all game assets
 * Generates placeholder assets for MVP
 */

import Phaser from 'phaser';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { T, INK, PALETTE, FONT, paintBackground, fadeInScene } from '../constants/Theme';

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.PRELOAD });
  }

  preload(): void {
    // Create loading bar
    this.createLoadingBar();

    // Generate placeholder chess piece sprites
    this.generatePlaceholderPieces();

    // Load Lozza worker (if available)
    // Note: In real deployment, this would be in public/assets/workers/
    console.log('Note: Lozza AI will use fallback if worker not found');
  }

  create(): void {
    console.log('Assets loaded');
    this.scene.start(SCENES.MENU);
  }

  private createLoadingBar(): void {
    const width = GAME_CONFIG.WIDTH;
    const height = GAME_CONFIG.HEIGHT;
    const cx = width / 2;
    const cy = height / 2;
    const barW = 340, barH = 22, radius = 11;

    paintBackground(this, width, height);
    fadeInScene(this);

    const crest = this.add.text(cx, cy - 110, '♚', { fontFamily: FONT, fontSize: '110px', color: INK.gold });
    crest.setOrigin(0.5);
    crest.setShadow(0, 5, '#0a0f1c', 12, false, true);

    const loadingText = this.add.text(cx, cy + 70, 'Loading…', T.body({ color: INK.secondary }));
    loadingText.setOrigin(0.5);

    const box = this.add.graphics();
    box.fillStyle(PALETTE.panel, 1);
    box.fillRoundedRect(cx - barW / 2, cy - barH / 2, barW, barH, radius);
    box.lineStyle(2, PALETTE.stroke, 1);
    box.strokeRoundedRect(cx - barW / 2, cy - barH / 2, barW, barH, radius);

    const progressBar = this.add.graphics();
    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(PALETTE.gold, 1);
      const w = Math.max(0, (barW - 6) * value);
      if (w > 0) progressBar.fillRoundedRect(cx - barW / 2 + 3, cy - barH / 2 + 3, w, barH - 6, radius - 3);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      box.destroy();
      loadingText.destroy();
      crest.destroy();
    });
  }

  private generatePlaceholderPieces(): void {
    // Generate chess piece sprites using Unicode symbols
    const pieceTypes = ['p', 'n', 'b', 'r', 'q', 'k'];

    // Unicode chess symbols - use filled versions for both colors
    const whitePieceIcons: Record<string, string> = {
      k: '♔',  // White King
      q: '♕',  // White Queen
      r: '♖',  // White Rook
      b: '♗',  // White Bishop
      n: '♘',  // White Knight
      p: '♙',  // White Pawn
    };

    const blackPieceIcons: Record<string, string> = {
      k: '♚',  // Black King
      q: '♛',  // Black Queen
      r: '♜',  // Black Rook
      b: '♝',  // Black Bishop
      n: '♞',  // Black Knight
      p: '♟',  // Black Pawn
    };

    // Higher-resolution textures with baked depth (drop shadow + radial
    // gradient disc + rim light) so pieces read as premium tokens, not flat
    // glyphs. Extra padding at the bottom holds the soft shadow.
    const texSize = 144;
    const center = texSize / 2;
    const radius = texSize * 0.40;

    for (const type of pieceTypes) {
      this.buildPieceTexture(`w${type}`, whitePieceIcons[type], texSize, center, radius, {
        top: 0xffffff, bottom: 0xc9d2dc, rim: 0xffffff, glyph: '#1b2433', glyphShadow: 0xffffff,
      });
      this.buildPieceTexture(`b${type}`, blackPieceIcons[type], texSize, center, radius, {
        top: 0x3c4f68, bottom: 0x1f2c3f, rim: 0x6781a0, glyph: '#f3f6fb', glyphShadow: 0x0a0f1c,
      });
    }

    console.log('Generated chess piece sprites');
  }

  /** Build a single piece texture: soft shadow, radial-gradient disc, rim, glyph. */
  private buildPieceTexture(
    key: string,
    glyph: string,
    texSize: number,
    center: number,
    radius: number,
    c: { top: number; bottom: number; rim: number; glyph: string; glyphShadow: number }
  ): void {
    const rt = this.add.renderTexture(0, 0, texSize, texSize);
    const cy = center - 4; // lift the disc so its shadow sits below

    // Soft drop shadow (stacked translucent circles)
    const shadow = this.add.graphics();
    for (let i = 6; i >= 1; i--) {
      shadow.fillStyle(0x000000, 0.05);
      shadow.fillCircle(center, cy + 9, radius + i * 1.5);
    }
    rt.draw(shadow, 0, 0); shadow.destroy();

    // Radial-gradient disc faked with concentric circles (top->bottom blend by y)
    const disc = this.add.graphics();
    const steps = 14;
    for (let i = steps; i >= 0; i--) {
      const t = i / steps;
      const col = this.lerpColor(c.bottom, c.top, t);
      disc.fillStyle(col, 1);
      // offset the inner circles slightly upward for a lit-from-above feel
      disc.fillCircle(center, cy - (1 - t) * radius * 0.22, radius * (0.4 + 0.6 * t));
    }
    rt.draw(disc, 0, 0); disc.destroy();

    // Rim light + outline
    const rim = this.add.graphics();
    rim.lineStyle(3, c.rim, 0.55);
    rim.strokeCircle(center, cy, radius);
    rim.lineStyle(2, 0x0a0f1c, 0.35);
    rim.strokeCircle(center, cy, radius + 1.5);
    rt.draw(rim, 0, 0); rim.destroy();

    // Glyph with a faint same-direction highlight for legibility
    const shadowText = this.add.text(0, 0, glyph, {
      fontSize: '90px', color: '#' + c.glyphShadow.toString(16).padStart(6, '0'),
      fontFamily: 'Arial, sans-serif',
    });
    shadowText.setOrigin(0.5).setAlpha(0.35);
    rt.draw(shadowText, center, cy + 2);
    shadowText.destroy();

    const text = this.add.text(0, 0, glyph, {
      fontSize: '90px', color: c.glyph, fontFamily: 'Arial, sans-serif',
    });
    text.setOrigin(0.5);
    rt.draw(text, center, cy);
    text.destroy();

    rt.saveTexture(key);
    rt.destroy();
  }

  private lerpColor(a: number, b: number, t: number): number {
    const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
    const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
    return ((Math.round(ar + (br - ar) * t) << 16) |
      (Math.round(ag + (bg - ag) * t) << 8) |
      Math.round(ab + (bb - ab) * t));
  }
}
